import { 
    AnalysisOrPlanResult, 
    CrisisManagementPlanResult, 
    CrisisManagementFrameworkResult,
    BusinessResiliencyPlanResult, 
    BusinessResiliencyFrameworkResult,
    EmergencyPreparednessFrameworkResult,
    EmergencyPreparednessPlanResult, 
    LocalIncidentManagementResult, 
    RouteAnalysisResult, 
    EnterpriseFsatResult, 
    FSATData, 
    EnterpriseAuditResult, 
    IntegratedAnalysisResult, 
    EventSecurityPlanResult, 
    EpThreatAnalysisResult, 
    StaffProtectionPlanResult,
    TravelSecurityBriefResult,
    HSSEComplianceBriefing,
    HSSEIncidentReport,
    EpOpsAnalysisResult,
    GlobalThreatGroupAnalysisResult,
    GeopoliticalGovernmentAnalysisResult,
    CapstoneFeedbackResult,
    FacilityProtectionResult,
    ItineraryRiskResult,
    CompositeReportResult,
    AuditRemediationResult,
    TextAnalysisResult,
    GeopoliticalAnalysisResult,
    EpAdvanceReport,
    EpPIBriefResult,
    SrmProject
} from '@types';

export function isCrisisManagementPlanResult(r: any): r is CrisisManagementPlanResult { return r?.reportType === 'CrisisManagementPlanResult'; }
export function isCrisisManagementFrameworkResult(r: any): r is CrisisManagementFrameworkResult { return r?.reportType === 'CrisisManagementFrameworkResult'; }
export function isBusinessResiliencyPlanResult(r: any): r is BusinessResiliencyPlanResult { return r?.reportType === 'BusinessResiliencyPlanResult'; }
export function isBusinessResiliencyFrameworkResult(r: any): r is BusinessResiliencyFrameworkResult { return r?.reportType === 'BusinessResiliencyFrameworkResult'; }
export function isEmergencyPreparednessFrameworkResult(r: any): r is EmergencyPreparednessFrameworkResult { return r?.reportType === 'EmergencyPreparednessFrameworkResult'; }
export function isEmergencyPreparednessPlanResult(r: any): r is EmergencyPreparednessPlanResult { return r?.reportType === 'EmergencyPreparednessPlanResult'; }
export function isLocalIncidentManagementResult(r: any): r is LocalIncidentManagementResult { return r?.reportType === 'LocalIncidentManagementResult'; }
export function isRouteAnalysisResult(r: any): r is RouteAnalysisResult { return r?.reportType === 'RouteAnalysisResult'; }
export function isEnterpriseFsatResult(r: any): r is EnterpriseFsatResult { return r?.reportType === 'EnterpriseFsatResult'; }
export function isFSATReport(r: any): r is FSATData { return r?.type === 'FSATReport'; }
export function isEnterpriseAuditResult(r: any): r is EnterpriseAuditResult {
    // Primary: explicit reportType stamp (set by backend after fix)
    if (r?.reportType === 'EnterpriseRiskAudit') return true;
    // Heuristic fallback: covers cached/older results that lack the stamp
    // but contain the characteristic MLCOA/MDCOA structure from the audit engine
    return !!(r?.mdcoa !== undefined && r?.mlcoa !== undefined && r?.remediationActions !== undefined);
}
export function isIntegratedAnalysisResult(r: any): r is IntegratedAnalysisResult { return r?.reportType === 'IntegratedAnalysisResult' || r?.reportType === 'IntegratedAnalysis'; }
export function isEventSecurityPlanResult(r: any): r is EventSecurityPlanResult { return r?.reportType === 'EventSecurityPlanResult'; }
export function isEpThreatAnalysisResult(r: any): r is EpThreatAnalysisResult { return r?.reportType === 'EpThreatAnalysisResult'; }
export function isStaffProtectionPlanResult(r: any): r is StaffProtectionPlanResult { return r?.reportType === 'StaffProtectionPlanResult'; }
export function isTravelSecurityBriefResult(r: any): r is TravelSecurityBriefResult { return r?.reportType === 'TravelSecurityBriefResult'; }
export function isHSSEBriefing(r: any): r is HSSEComplianceBriefing { return r !== null && !!r.reportType && r.reportType === 'HSSEComplianceBriefing'; }
export function isHSSEIncident(r: any): r is HSSEIncidentReport { return r !== null && !!r.reportType && r.reportType === 'HSSEIncidentReport'; }
export function isEpOpsResult(r: any): r is EpOpsAnalysisResult { return r !== null && !!r.reportType && r.reportType === 'EpOpsAnalysisResult'; }
export function isGlobalThreatGroupAnalysisResult(r: any): r is GlobalThreatGroupAnalysisResult { return r !== null && !!r.reportType && r.reportType === 'GlobalThreatGroupAnalysis'; }
export function isGeopoliticalGovernmentAnalysisResult(r: any): r is GeopoliticalGovernmentAnalysisResult { return r !== null && !!r.reportType && r.reportType === 'GeopoliticalGovernmentAnalysis'; }
export function isCapstoneFeedbackResult(r: any): r is CapstoneFeedbackResult { return r !== null && !!r.reportType && r.reportType === 'CapstoneFeedbackResult'; }
export function isFacilityProtectionResult(r: any): r is FacilityProtectionResult { return r?.reportType === 'FacilityProtectionResult'; }
export function isItineraryRiskResult(r: any): r is ItineraryRiskResult {
    // Primary: explicit reportType stamp from the backend prompt
    if (r?.reportType === 'ItineraryRiskResult') return true;
    // Structural heuristic: covers cases where AI omits/corrupts the reportType field
    // but still returns the characteristic 'segments' array with itinerary leg data.
    return !!(Array.isArray(r?.segments) && r?.segments.length > 0 && r?.segments[0]?.legName !== undefined);
}
export function isCompositeReportResult(r: any): r is CompositeReportResult { return r?.reportType === 'CompositeReportResult'; }
export function isAuditRemediationResult(r: any): r is AuditRemediationResult { return r?.reportType === 'AuditRemediationResult'; }
export function isDailyMicroReport(r: any): boolean { return r?.reportType === 'DailyMicroReport' || r?.reportType === 'CountryRiskProfile' || r?.type === 'daily_micro'; }
export function isGeopoliticalAnalysis(r: any): r is any { return r?.reportType === 'MacroGeopoliticalAnalysis' || r?.type === 'geopolitical_dossier'; }
export function isComprehensiveGeopoliticalAnalysis(r: any): r is any { return r?.reportType === 'ComprehensiveGeopoliticalAnalysis'; }
export function isGeopoliticalAnalysisResult(r: any): r is GeopoliticalAnalysisResult { return r?.reportType === 'GeopoliticalAnalysis'; }
export function isTextResult(r: any): r is TextAnalysisResult { return r !== null && 'summary' in r && 'sections' in r && (!r.reportType || r.reportType === 'TextAnalysisResult'); }
export function isSrmBlueprint(r: any): r is SrmProject { return r !== null && (r.reportType === 'SrmBlueprint' || (Array.isArray(r.modules) && 'locationName' in r)); }
export function isSrmAnalysis(r: any): boolean { return r !== null && (r.reportType === 'IntegratedAnalysisResult' || r.reportType === 'IntegratedAnalysis') && (!!r.securityRiskManagementPlan || !!r.epDetail); }

// EP Hybrid Integration guards
export function isEpAdvanceReport(r: any): r is EpAdvanceReport {
    // Primary: explicit reportType stamp from backend
    if (r?.reportType === 'EpAdvanceReport') return true;
    // Structural heuristic: characteristic fields of the advance survey schema
    return !!(r?.operationName !== undefined && r?.overallReadiness !== undefined && r?.siteAssessment !== undefined && r?.entryEgressPlan !== undefined);
}
export function isEpPIBriefResult(r: any): r is EpPIBriefResult {
    // Primary: explicit reportType stamp from backend
    if (r?.reportType === 'EpPIBriefResult') return true;
    // Structural heuristic: characteristic fields of the PI brief schema
    // Note: must NOT match EpAdvanceReport, so we require protectiveMeasures (array) + aoIncidents
    return !!(Array.isArray(r?.protectiveMeasures) && r?.aoIncidents !== undefined && r?.contingencyActions !== undefined && r?.overallAssessment !== undefined);
}
